import { CommonModule, KeyValuePipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import { formatHHmm } from '../event-date-time-form.utils';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'mat-error[siatErrorField]',
  templateUrl: './error-field.component.html',
  imports: [CommonModule, KeyValuePipe],
  host: {
    class: 'siat-error-field',
  },
  styles: `
    :host {
      display: block;
      font-size: 0.8125rem !important;
      line-height: 1.25 !important;
    }
    :host.mat-mdc-form-field-bottom-align::before {
      content: none !important;
      height: 0 !important;
    }
  `,
})
export class ErrorFieldComponent {
  /**
   * The control associated with the error field.
   */
  control = input.required<AbstractControl>();

  /**
   * The field's label, used to compose the "required" error message.
   */
  label = input<string | null | undefined>('');

  translate = inject(TranslateService);

  /**
   * Ticks on every value/status/touched/pristine event of the current
   * control, so `errors`/`hasErrors` stay reactive under zoneless change
   * detection: marking a control as touched (e.g. on blur) doesn't
   * otherwise notify anything watching plain getters.
   */
  private readonly controlEvents = toSignal(
    toObservable(this.control).pipe(switchMap((control) => control.events)),
  );

  /**
   * The errors of the control.
   */
  errors = computed<ValidationErrors | null>(() => {
    this.controlEvents();
    return this.control().errors;
  });

  /**
   * A flag telling whether the control has errors or not.
   */
  hasErrors = computed(() => {
    this.controlEvents();
    const control = this.control();
    return (
      control.invalid &&
      (control.dirty ||
        control.touched ||
        control.hasError('matTimepickerMin') ||
        control.hasError('matTimepickerMax'))
    );
  });

  /**
   * Returns the error message based on the control errors.
   */
  getErrorMessage(): string {
    const control = this.control();
    if (control.hasError('required')) {
      const label = this.label();
      return label
        ? this.translate.instant('FORM_VALIDATION.REQUIRED_FIELD', { label })
        : this.translate.instant('FORM_VALIDATION.REQUIRED');
    }
    if (control.hasError('minlength')) {
      return this.translate.instant('FORM_VALIDATION.MIN_LENGTH', {
        requiredLength: control.getError('minlength').requiredLength,
      });
    }
    if (control.hasError('maxlength')) {
      return this.translate.instant('FORM_VALIDATION.MAX_LENGTH', {
        requiredLength: control.getError('maxlength').requiredLength,
      });
    }
    if (control.hasError('email')) {
      return this.translate.instant('FORM_VALIDATION.EMAIL');
    }
    if (control.hasError('matTimepickerMin')) {
      return this.translate.instant('FORM_VALIDATION.MIN_TIME', {
        min: formatHHmm(control.getError('matTimepickerMin').min),
      });
    }
    if (control.hasError('matTimepickerMax')) {
      return this.translate.instant('FORM_VALIDATION.MAX_TIME', {
        max: formatHHmm(control.getError('matTimepickerMax').max),
      });
    }
    if (control.hasError('min')) {
      return this.translate.instant('FORM_VALIDATION.MIN', {
        min: control.getError('min').min,
      });
    }
    if (control.hasError('max')) {
      return this.translate.instant('FORM_VALIDATION.MAX', {
        max: control.getError('max').max,
      });
    }
    if (control.hasError('whitespace')) {
      return this.translate.instant('FORM_VALIDATION.WHITESPACE');
    }
    if (control.hasError('pattern')) {
      const patternError = control.getError('pattern');
      return (
        patternError?.message ||
        'Il valore non corrisponde al formato richiesto'
      );
    }
    if (control.hasError('invalidCodiceFiscale')) {
      return this.translate.instant('FORM_VALIDATION.INVALID_CODICE_FISCALE');
    }
    return '';
  }
}
